<script setup lang="ts">
/**
 * TopBar — horizontal header above the content area.
 *
 * Layout (left to right):
 *   - Breadcrumb (current route's meta.crumb)
 *   - Global search input (cmd-K shortcut, placeholder for now)
 *   - Lens chip
 *   - Notifications bell (placeholder badge)
 *   - User menu (avatar + dropdown for logout)
 *
 * Mirrors the prototype's top header strip pattern.
 */
import { Bell, Search, LogOut } from 'lucide-vue-next'
import { useRouter } from 'vue-router'
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverPortal,
  PopoverContent,
} from 'reka-ui'
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import Breadcrumb from './Breadcrumb.vue'
import LensSwitcher from './LensSwitcher.vue'

const router = useRouter()
const auth = useAuthStore()

const userInitials = computed(() => {
  const name = auth.user?.userName ?? ''
  return name.slice(0, 2).toUpperCase() || '?'
})

async function handleLogout() {
  await auth.logout()
  router.push({ name: 'login' })
}
</script>

<template>
  <header
    class="h-14 px-4 flex items-center gap-3 bg-bg border-b border-border shrink-0"
  >
    <Breadcrumb />

    <div class="flex-1" />

    <!-- Global search (placeholder; cmd-K shortcut in Sprint 2) -->
    <div
      class="hidden md:flex items-center gap-2 h-8 px-2.5 w-72 max-w-sm
             bg-surface-2 border border-border rounded text-text-4 cursor-pointer
             hover:border-border-strong transition-colors"
    >
      <Search :size="14" />
      <span class="text-xs flex-1 truncate">{{ $t('topbar.search') }}</span>
      <kbd
        class="text-[9px] font-mono px-1.5 py-0.5 bg-surface-3 rounded-sm text-text-4"
      >⌘K</kbd>
    </div>

    <LensSwitcher />

    <!-- Notifications bell -->
    <button
      type="button"
      class="relative w-8 h-8 flex items-center justify-center text-text-3
             hover:text-text-1 hover:bg-surface-2 rounded transition-colors
             focus-visible:outline-none focus-visible:shadow-focus"
      :aria-label="$t('topbar.notifications')"
    >
      <Bell :size="15" />
      <span
        class="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-brand"
        aria-hidden="true"
      />
    </button>

    <!-- User menu -->
    <PopoverRoot>
      <PopoverTrigger data-testid="user-menu"
        class="w-8 h-8 rounded-full bg-surface-3 text-text-2 flex items-center
               justify-center text-[10px] font-semibold uppercase
               hover:bg-surface-2 hover:text-text-1 transition-colors
               focus-visible:outline-none focus-visible:shadow-focus"
        :aria-label="$t('topbar.userMenu')"
      >{{ userInitials }}</PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          :side-offset="6"
          align="end"
          class="w-56 p-1.5 bg-surface border border-border-strong rounded-lg
                 shadow-lg z-50 outline-none"
        >
          <div class="px-2 py-2 border-b border-border mb-1.5">
            <p class="text-sm font-semibold text-text-1 m-0 truncate">
              {{ auth.user?.userName ?? '—' }}
            </p>
            <p class="text-xs text-text-4 m-0 truncate">
              {{ $t('account.privilegesCount', { n: auth.user?.privileges.length ?? 0 }) }}
            </p>
          </div>
          <button
            type="button"
            class="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-text-2
                   hover:bg-surface-2 hover:text-text-1 rounded transition-colors
                   text-left"
            @click="handleLogout"
          >
            <LogOut :size="13" />
            <span>{{ $t('topbar.logout') }}</span>
          </button>
        </PopoverContent>
      </PopoverPortal>
    </PopoverRoot>
  </header>
</template>
